import type { MenuItem, TpaPageLink } from '@wix/document-services-types';
import { HttpClient } from '@wix/http-client';
import type { ComponentRef, EditorSDK } from '@wix/platform-editor-sdk';

import { getSiteLocale } from '../../../../utils/locale';
import { APP_TOKEN, MENU_IDS, SANTA_MEMBERS_APP_ID } from '../../../constants';
import { monitoredNavigateToMemberSettingsPage } from '../../../platform-app/split-ma/editor-ready-utils/monitored-methods';
import { connectLoginButton } from '../../../wrappers/components';
import { setIsMigration } from '../../applicationState';

type WidgetTitle = {
  title: string;
  widgetId: string;
};

type WidgetTitles = {
  appDefinitionId: string;
  widgets: WidgetTitle[];
};

const LOGIN_BAR_COMP_TYPE = 'wysiwyg.viewer.components.LoginSocialBar';
const LOGIN_BAR_CONTROLLER_TYPE = 'members-login-bar';

const uninstallMembersAreaV2 = async (editorSDK: EditorSDK) => {
  await editorSDK.application.uninstall(APP_TOKEN, {
    openConfirmation: false,
  });
};

const installMembersAreaV3 = async (editorSDK: EditorSDK) => {
  await editorSDK.document.application.add(APP_TOKEN, {
    appDefinitionId: SANTA_MEMBERS_APP_ID,
    originInfo: {
      migrationData: {
        isMigrating: true,
      },
    },
  });
};

type LoginBarInstance = {
  controllerRef: ComponentRef;
  loginBarRef: ComponentRef;
};

const getLoginBarInstances = async (editorSDK: EditorSDK): Promise<LoginBarInstance[]> => {
  const controllers = await (editorSDK as any).document.controllers.findAllByType(APP_TOKEN, {
    controllerType: LOGIN_BAR_CONTROLLER_TYPE,
  });

  if (!controllers || controllers.length === 0) throw new Error('[MA v3 migration]: Members login bar controller not found');
  

  return Promise.all(
    controllers.map(async (controller: any) => {
      const controllerRef = controller?.controllerRef as ComponentRef | undefined;
      if (!controllerRef)  throw new Error('[MA v3 migration]: Members login bar controller ref missing');
      

      const controllerStructure = await (editorSDK as any).document.components.serialize(APP_TOKEN, {
        componentRef: controllerRef,
        maintainIdentifiers: true,
      });

      const loginBarComponent = controllerStructure.components?.find(
        (component: any) => component?.componentType === LOGIN_BAR_COMP_TYPE,
      );

      if (!loginBarComponent?.id) throw new Error('[MA v3 migration]: Missing login bar for controller');

      const loginBarRef = await editorSDK.components.getById(APP_TOKEN, { id: loginBarComponent.id });
      if (!loginBarRef) throw new Error('[MA v3 migration]: Login bar component ref not found');

      const connections = await editorSDK.controllers.listConnections(APP_TOKEN, {
        componentRef: loginBarRef,
      });

      const finalControllerRef = connections[0]?.controllerRef || controllerRef;
      return { controllerRef: finalControllerRef, loginBarRef } satisfies LoginBarInstance;
  
    }),
  );
};

const disconnectLoginBars = async (editorSDK: EditorSDK, instances: LoginBarInstance[]) => {
  if (instances.length === 0) throw new Error('[MA v3 migration]: Members login bar controller not found');

  await Promise.all(
    instances.map(async ({ loginBarRef, controllerRef }) => {
      await editorSDK.components.data.update(APP_TOKEN, {
        componentRef: loginBarRef,
        data: {
          iconItemsRef: null,
          menuItemsRef: null,
        },
      });

      await editorSDK.components.data.update(APP_TOKEN, {
        componentRef: controllerRef,
        data: {
          applicationId: 'placeholder',
        },
      });
    }),
  );
};

const reconnectLoginBars = async (editorSDK: EditorSDK, instances: LoginBarInstance[]) => {
  if (instances.length === 0) throw new Error('[MA v3 migration]: Members login bar controller not found');

  await Promise.all(
    instances.map(async ({ loginBarRef, controllerRef }) => {
      await editorSDK.components.data.update(APP_TOKEN, {
        componentRef: controllerRef,
        data: {
          applicationId: SANTA_MEMBERS_APP_ID,
        },
      });

      await connectLoginButton(editorSDK, loginBarRef, false);
    }),
  );
};

const getWidgetTitles = async (siteLocale: string) => {
  const translationsUrl = `https://editor.wix.com/_api/app-market-api/apps/page-apps?fields=widgets.title,widgets.widgetId,appDefinitionId&lang=${siteLocale}`;
  const httpClient = new HttpClient();
  const { data } = await httpClient.get<WidgetTitles[]>(translationsUrl);

  return data;
};

const getMenus = async (editorSDK: EditorSDK) => {
  return Promise.all([
    editorSDK.menu.getById(APP_TOKEN, { menuId: MENU_IDS.SUB_MENU_ID }),
    editorSDK.menu.getById(APP_TOKEN, { menuId: MENU_IDS.SETTINGS_SUB_MENU_ID }),
    editorSDK.menu.getById(APP_TOKEN, { menuId: MENU_IDS.LOGIN_MENU_ID }),
  ]);
};

const getTranslatedMenuItems = async (editorSDK: EditorSDK, menuItems: MenuItem[], widgetTitles: WidgetTitles[]) => {
  const translatedMenuItems: MenuItem[] = [];

  menuItems.forEach((item) => {
    let translatedLabel = '';

    for (const app of widgetTitles) {
      const widgetTitle = app.widgets.find(({ widgetId }) => widgetId === (item.link as TpaPageLink)?.itemId);

      if (widgetTitle) {
        translatedLabel = widgetTitle.title;
        break;
      }
    }

    translatedMenuItems.push({
      ...item,
      label: translatedLabel || item.label,
    });
  });

  return translatedMenuItems;
};

const updateMenusWithTranslatedItems = async (editorSDK: EditorSDK) => {
  const siteLocale = await getSiteLocale(editorSDK);

  if (siteLocale === 'en') {
    return;
  }

  const widgetTitles = await getWidgetTitles(siteLocale);
  const menus = await getMenus(editorSDK);

  await Promise.all(
    menus.map(async (menu) => {
      const translatedMenuItems = await getTranslatedMenuItems(editorSDK, menu.items, widgetTitles);

      await editorSDK.menu.update(APP_TOKEN, {
        menuId: menu.id,
        menuData: {
          items: translatedMenuItems,
        },
      });
    }),
  );
};

export const migrateToV3OnTemplates = async (editorSDK: EditorSDK) => {
  try {
    setIsMigration(true);

    console.log('[MA v3 migration]: Starting migration to V3');

    const loginBarInstances = await getLoginBarInstances(editorSDK);
    await disconnectLoginBars(editorSDK, loginBarInstances);
    await uninstallMembersAreaV2(editorSDK);
    await installMembersAreaV3(editorSDK);

    await reconnectLoginBars(editorSDK, loginBarInstances);
    await updateMenusWithTranslatedItems(editorSDK);
    await monitoredNavigateToMemberSettingsPage(editorSDK);

    console.log('[MA v3 migration]: Migration to V3 finished');
  } catch (e) {
    console.error('[MA v3 migration]: Migration to V3 failed', e);
    throw e;
  } finally {
    setIsMigration(false);
  }
};
